/**
 * Registry Service
 * Interacts with the network-mcp registry server for agent discovery and registration
 */

import type { AgentCard } from '@/types/contact.js';

const REGISTRY_URL = 'https://zestful-beauty-production.up.railway.app';

export interface ServiceDescriptor {
    id: string;
    name: string;
    description: string;
    url: string;
    discoveryType: 'a2a' | 'mcp';
    protocols: Array<{
        protocol: string;
        endpoint: string;
        version?: string;
    }>;
    capabilities: {
        streaming?: boolean;
        tags: string[];
    };
    owner: {
        userId: string;
        username: string;
        email?: string;
    };
    visibility: 'public' | 'private';
    registeredAt: string;
    lastSeen: string;
}

export interface ServiceQuery {
    query?: string;
    discoveryType?: 'a2a' | 'mcp';
    tags?: string[];
    protocols?: string[];
    limit?: number;
    offset?: number;
}

export interface ServiceDiscoveryResult {
    services: ServiceDescriptor[];
    total: number;
    query: ServiceQuery;
}

export interface ServiceRegistrationRequest {
    url: string;
    owner: {
        userId: string;
        username: string;
        email?: string;
    };
    visibility?: 'public' | 'private';
    manual?: {
        name: string;
        description: string;
        discoveryType: 'a2a' | 'mcp';
        tags?: string[];
    };
}

/**
 * Discover services from the registry
 */
export async function discoverServices(query: ServiceQuery = {}): Promise<ServiceDiscoveryResult> {
    const params = new URLSearchParams();

    if (query.query) params.append('query', query.query);
    if (query.discoveryType) params.append('discoveryType', query.discoveryType);
    if (query.tags) params.append('tags', query.tags.join(','));
    if (query.protocols) params.append('protocols', query.protocols.join(','));
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.offset) params.append('offset', query.offset.toString());

    const response = await fetch(`${REGISTRY_URL}/services?${params.toString()}`);

    if (!response.ok) {
        throw new Error(`Failed to discover services: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get a specific service by ID
 */
export async function getService(serviceId: string): Promise<ServiceDescriptor> {
    const response = await fetch(`${REGISTRY_URL}/services/${serviceId}`);

    if (!response.ok) {
        throw new Error(`Failed to get service: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Register/publish an agent on the registry
 */
export async function registerService(request: ServiceRegistrationRequest): Promise<{ success: boolean; serviceId: string; descriptor: ServiceDescriptor }> {
    const response = await fetch(`${REGISTRY_URL}/services`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.message || error.error || 'Failed to register service');
    }

    return response.json();
}

/**
 * Unregister a service from the registry
 */
export async function unregisterService(serviceId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${REGISTRY_URL}/services/${serviceId}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error(`Failed to unregister service: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Convert a ServiceDescriptor to an AgentCard for contact storage
 */
export function serviceDescriptorToAgentCard(service: ServiceDescriptor): AgentCard {
    // Find A2A protocol if available
    const a2aProtocol = service.protocols.find(p =>
        p.protocol === 'a2a-jsonrpc' || p.protocol === 'jsonrpc'
    );

    return {
        protocolVersion: '0.3.0',
        name: service.name,
        description: service.description,
        url: service.url,
        version: '1.0.0',
        preferredTransport: a2aProtocol ? 'JSONRPC' : 'HTTP+JSON',
        defaultInputModes: ['text'],
        defaultOutputModes: ['text'],
        skills: [], // ServiceDescriptor doesn't include detailed skills
        capabilities: {
            streaming: service.capabilities.streaming,
        },
        metadata: {
            dexto: {
                owner: service.owner,
                delegation: a2aProtocol ? {
                    protocol: 'a2a-jsonrpc',
                    endpoint: a2aProtocol.endpoint,
                    supportsSession: true,
                    supportsStreaming: service.capabilities.streaming,
                } : undefined,
            },
            registry: {
                serviceId: service.id,
                discoveryType: service.discoveryType,
                tags: service.capabilities.tags,
            }
        },
    };
}

/**
 * Fetch agent card from a service URL
 */
export async function fetchAgentCardFromService(serviceUrl: string): Promise<AgentCard> {
    const agentCardUrl = new URL('/.well-known/agent-card.json', serviceUrl);
    const response = await fetch(agentCardUrl.toString());

    if (!response.ok) {
        throw new Error(`Failed to fetch agent card: ${response.statusText}`);
    }

    return response.json();
}
